/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ApiService } from '@classes/apiClasses';
import { IListResponse, IResponse } from '@interfaces/services';
import { ICasinoSubscription, IUserSubscription } from '@interfaces/userSubscription';
import { Get, Post } from 'lib/axiosWrapper';
import { IServiceReturn } from './common';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { IFilterState } from 'appRedux/createStore';
import { ca } from 'date-fns/locale';
import { SupportedPhoneCountryConfig } from 'common/phoneCountries';

export interface ICheckoutResponse {
  id: string;
}

export interface ICasinoLinkedAccountTier {
  tierId: string;
  tierName: string;
  tierDateLastChecked?: string;
}

export interface ICasinoLinkedAccount {
  appUserPk: string;
  casinoId: number;
  playerId: string;
  createDate?: number;
  birthday?: string;
  tier?: ICasinoLinkedAccountTier;
  verification?: string;
  verifiedDate?: string;
  acknowledgedDate?: number;
}

export interface ILinkedAccounts extends IServiceReturn {
  user: {
    linkedAccounts: Array<ICasinoLinkedAccount>;
  };
}

export interface IPlayerCardInfo {
  rankingName: string | null;
  rankingNumber: number;
  freeplayBalance: number | null;
  showFreeplayBalance: boolean;
  pointBalance: number | null;
  showPointBalance: boolean;
  dateEnrolled: string | null;
  title: string | null;
}

export interface IUser extends IServiceReturn {
  user: {
    info: IUserInfo;
    subscriptions: IUserSubscription[];
    preference: object;
    linkedAccounts: Array<ICasinoLinkedAccount>;
    hasAccess: boolean;
    timeLeft: number;
    playerCardInfo?: IPlayerCardInfo | null;
  };
}

export interface ICasinoAccessInfo {
  hasAccess: boolean;
  timeLeft: number;
}

export interface ICasinoAccess {
  [casinoId: number]: ICasinoAccessInfo;
}

export interface ICasinoSchemaWithLastVisit extends ICasinoSchema {
  lastVisit?: string;
}
export interface IUserCasinosList {
  otherCasinos: Array<ICasinoSchemaWithLastVisit>;
  primaryCasino: ICasinoSchema;
  recentCasinos: Array<ICasinoSchemaWithLastVisit>;
}
export interface IUserInfoAndAccess extends IServiceReturn {
  user: {
    info: IUserInfo;
    subscription: IUserSubscription;
    linkedAccounts: Array<ICasinoLinkedAccount>;
    casinoAccess: ICasinoAccess;
    userCasinoList: IUserCasinosList;
  };
}

interface UserData {
  cognitoUserName: string;
  userDetails: IUserInfo;
  aliasInitial: string;
  aliasInitial2: string;
  aliasName: string;
}

export interface IUserSignup extends IServiceReturn {
  code: number;
  data: UserData;
  message: string;
  errorCode?: number;
  errorMsg?: string;
}

export interface ILinkedAccount {
  casinoId: number;
  casino_fk: number;
  createDate: number;
  playerId: number;
  birthday: string;
}

export interface ILoginResponse {
  user: IUserInfo & { cellNumber?: string };
  lastLogin: ILastLogin;
  token?: string;
}

export interface IEmulatePlayerSearchRow {
  appUserPk: string;
  aliasName: string;
  avatarId?: number;
  avatarBgId?: number;
  aliasInitial?: string;
  aliasInitial2?: string;
}

export interface ISearchPlayersForEmulateResponse {
  players: IEmulatePlayerSearchRow[];
}

export interface IEmulatePlayerTokensResponse {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  tokenType?: string;
}

export interface ILookupCasinoAccountCasino {
  casinoId: number | null;
  casinoName: string;
  shortName: string;
}

export interface ILookupCasinoAccount {
  email: string;
  cognitoUsername: string;
  sub?: string;
  createdAt?: string | null;
  casinos: ILookupCasinoAccountCasino[];
  casinoNames: string[];
}

export interface ILookupCasinoAccountResponse {
  found: boolean;
  message?: string;
  account?: ILookupCasinoAccount;
}

export interface IDeleteCasinoAccountResponse {
  status: 'deleted' | 'not_found' | 'refused' | 'partial';
  message?: string;
  reason?: string;
  steps?: Record<string, string>;
  email?: string;
  casinoNames?: string[];
}

export interface ILastLogin {
  last_login_timestamp: string;
  last_login_timestamp_counter: number;
}

export interface IAliasInfo {
  aliasInitial: string;
  aliasInitial2: string;
  aliasName: string;
}

export interface IUserInfo extends IAliasInfo {
  createDate: number;
  cognitoUserName: string;
  appUserPk: string;
  isPublicVisible: boolean;
  hasCompletedOnboarding?: boolean;
  phone: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  playerId?: number;
  birthday?: string;
  isCasinoAccount?: boolean;
  /** Opt-in Explore slot-reel loading cards (users.showLoadingReels). */
  showLoadingReels?: number | string | boolean | null;
  casinos?: number[]; // TODO: casino chooser will use this to display what casinos are visible to user
  linkedAccounts?: ILinkedAccount[];
  messages?: {
    templateKey: string;
    shownDate: number;
  }[];
  avatarId?: number;
  avatarBgId?: number;
}

export interface IUserMessage {
  _id?: string;
  templateKey?: string;
  templatekey?: string;
  key?: string;
  title?: string;
  description?: string;
  shownDate?: number;
  shown?: number;
  isVisible?: boolean;
  appUserPk?: string;
  cognitoUserName?: string;
}

export interface ISubscriptionInfo {
  appUserFk: string;
  subscriptionPlanFk: number;
  plan?: string;
  startDate: string;
  endDate: string;
}

export interface IGetMe extends IServiceReturn {
  user: {
    info: object;
    subscription: object;
    preference: object;
  };
}

export interface IUpdateMeResponse extends IServiceReturn {
  userInfo: Partial<IUserInfo>;
}

export interface IGetSupportedPhoneCountriesResponse {
  countries: SupportedPhoneCountryConfig[];
}

export class AuthService extends ApiService {
  constructor() {
    super('auth');
  }

  trackPreference(filters: Partial<IFilterState>) {
    return Post<ICheckoutResponse>(`${this.baseRoutePath}/trackPreference`, { filters }, {}, true);
  }

  Login() {
    return Post<IResponse<ILoginResponse>>(`${this.baseRoutePath}/Login`, {}, {}, true, true);
  }

  SearchPlayersForEmulate(query: string) {
    return Post<IResponse<ISearchPlayersForEmulateResponse>>(
      `${this.baseRoutePath}/SearchPlayersForEmulate`,
      { query },
      {},
      true
    );
  }

  EmulatePlayer(appUserPk: string) {
    return Post<IResponse<IEmulatePlayerTokensResponse>>(
      `${this.baseRoutePath}/EmulatePlayer`,
      { appUserPk },
      {},
      true
    );
  }

  LookupCasinoAccount(email: string) {
    return Post<IResponse<ILookupCasinoAccountResponse>>(
      `${this.baseRoutePath}/LookupCasinoAccount`,
      { email },
      {},
      true
    );
  }

  DeleteCasinoAccount(email: string, confirmEmail: string) {
    return Post<IResponse<IDeleteCasinoAccountResponse>>(
      `${this.baseRoutePath}/DeleteCasinoAccount`,
      { email, confirmEmail },
      {},
      true
    );
  }

  GetUserInfo(casinoIds: number[]) {
    return Get<IUserInfoAndAccess>(`${this.baseRoutePath}/GetUserInfo`, { casinoIds }, {}, true);
  }

  GetMe(casinoId: number) {
    return Get<IUser>(`${this.baseRoutePath}/GetMe`, { casinoId }, {}, true);
  }

  GetSupportedPhoneCountries() {
    return Post<IResponse<IGetSupportedPhoneCountriesResponse>>(
      `${this.baseRoutePath}/GetSupportedPhoneCountries`,
      {},
      {},
      false
    );
  }

  UpdateMe(userInfo: Partial<IUserInfo>) {
    return Post<IUpdateMeResponse>(`${this.baseRoutePath}/UpdateMe`, { userInfo }, {}, true);
  }

  UpdateUserMessageShownDate(_id?: string) {
    const body: any = {};
    if (typeof _id !== 'undefined') body._id = _id;
    return Post<{ status: string; shownDate: number }>(
      `${this.baseRoutePath}/UpdateUserMessageShownDate`,
      body,
      {},
      true
    );
  }

  GetUserMessages(appUserPk?: string, casinoId?: number) {
    const params: Record<string, unknown> = {};
    if (typeof appUserPk !== 'undefined') params.appUserPk = appUserPk;
    if (typeof casinoId !== 'undefined') params.casinoId = casinoId;
    return Get<IListResponse<IUserMessage>>(`${this.baseRoutePath}/GetUserMessages`, params, {}, true);
  }

  GetUserSubscriptionByCasinoId() {
    return Get<ICasinoSubscription>(
      `${this.baseRoutePath}/GetUserSubscriptionByCasinoId`,
      {},
      {},
      true
    );
  }

  GetUserSubscriptions() {
    return Get<IListResponse<IUserSubscription>>(
      `${this.baseRoutePath}/GetUserSubscriptions`,
      {},
      {},
      true
    );
  }

  GetLinkedAccounts(appUserFk: string) {
    return Get<ILinkedAccounts>(`${this.baseRoutePath}/GetLinkedAccounts`, { appUserFk }, {}, true);
  }

  AcknowledgeLinkedAccount(appUserFk: string, casinoId: number) {
    return Post<{ status: string; acknowledgedDate: number }>(
      `${this.baseRoutePath}/AcknowledgeLinkedAccount`,
      { appUserFk, casinoId },
      {},
      true
    );
  }

  CreateAccount(phoneNumber: string, password: string) {
    return Post<IUserSignup>(
      `${this.baseRoutePath}/CreateAccount`,
      { phoneNumber, password },
      {},
      false
    );
  }

  CreateCasinoUser(email: string, tempPassword: string, casinoKeys: (number | string)[]) {
    return Post<IUserSignup>(
      `${this.baseRoutePath}/CreateCasinoUser`,
      { email, tempPassword, casinoKeys },
      {},
      true
    );
  }
  
  /**
   * Create a user message for the current user if not exists
   */
  CreateUserMessage(params: {
    templateKey: string;
    title?: string;
    description?: string;
    isVisible?: boolean;
    casinoId?: number;
    appUserPk?: string;
  }) {
    return Post<{ status: string; inserted: boolean; Data?: any }>(
      `${this.baseRoutePath}/CreateUserMessage`,
      params,
      {},
      true
    );
  }
  RefreshToken(refreshToken: string) {
    return Post<{ token: string; expiresIn: number }>(
      `${this.baseRoutePath}/RefreshToken`,
      { refreshToken },
      {},
      false
    );
  }

  UpdateUserSubscription(userSubscription: ISubscriptionInfo) {
    return Post<ICheckoutResponse>(
      `${this.baseRoutePath}/UpdateUserSubscription`,
      userSubscription,
      {},
      true
    );
  }
}
